import React, { useState, useEffect } from "react";
import { Plus, X, Check } from "lucide-react";
import { StatusGroupItem } from "./StatusGroupItem";
import { Button } from "../ui/button";
import { IconType } from "./constants";
import { StatusGroupType } from "./StatusesContent";
import { ColumnConfig } from "./utils/columnConfigUtils";

interface StatusGroupProps {
  group: StatusGroupType;
  getStatusIcon: (status: string, iconType?: IconType) => React.ReactNode;
  onCreateStatus: (statusName: string, statusType: string) => void;
  onUpdateStatus: (statusId: string, newName: string) => Promise<void>;
  onDeleteStatus: (statusId: string) => void;
  columnConfig?: ColumnConfig[];
  newlyCreatedStatusId?: string | null;
}

export const StatusGroup: React.FC<StatusGroupProps> = React.memo(
  ({
    group,
    getStatusIcon,
    onCreateStatus,
    onUpdateStatus,
    onDeleteStatus,
    columnConfig = [],
    newlyCreatedStatusId,
  }) => {
    const [isCreatingStatus, setIsCreatingStatus] = useState(false);
    const [newStatusName, setNewStatusName] = useState("");

    useEffect(() => {
      if (
        newlyCreatedStatusId &&
        group.statuses.some((s) => s.id === newlyCreatedStatusId)
      ) {
        setIsCreatingStatus(true);
      }
    }, [newlyCreatedStatusId, group.statuses]);

    const handleCreateStatus = (name: string) => {
      if (name.trim()) {
        onCreateStatus(name.trim(), group.type);
        setIsCreatingStatus(false);
        setNewStatusName("");
      }
    };

    const handleCancelCreate = () => {
      setIsCreatingStatus(false);
      setNewStatusName("");
    };

    const handleAddStatusClick = () => {
      setIsCreatingStatus(true);
    };

    return (
      <div>
        <div className="flex items-center justify-between py-2 pl-4 pr-6 rounded-md bg-secondary-n-950">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-2xs text-secondary-n-50 font-medium">
              {group.name}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={handleAddStatusClick}
              className="flex items-center justify-center w-6 h-6 p-0 rounded text-secondary-n-600 hover:text-secondary-n-50"
              title="Add status to group"
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="px-2 py-1 pr-4 space-y-1">
          {isCreatingStatus && (
            <div className="flex gap-2 items-center pt-0 border my-1 pr-1 mr-1 border-transparent hover:border-secondary-n-600 focus-within:border-primary-0! rounded-md outline-hidden justify-between bg-secondary-n-950 ml-2">
              <input
                value={newStatusName}
                onChange={(e) => setNewStatusName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateStatus(newStatusName);
                  if (e.key === "Escape") handleCancelCreate();
                }}
                placeholder="Status name"
                className="w-full h-8 px-2 font-normal text-xs justify-between border-none! outline-hidden ring-0 focus:ring-0 focus:outline-hidden bg-transparent focus:bg-transparent placeholder:text-secondary-n-600"
                autoFocus
              />
              <div className="flex items-center gap-2 pr-1">
                <X
                  onClick={handleCancelCreate}
                  className="size-3.5 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
                />
                <Check
                  onClick={() => handleCreateStatus(newStatusName)}
                  className="size-3.5 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
                />
              </div>
            </div>
          )}

          {group.statuses.map((status) => (
            <div key={status.id} className="ml-2">
              <StatusGroupItem
                status={status}
                getStatusIcon={getStatusIcon}
                onUpdate={(newName) => onUpdateStatus(status.id, newName)}
                onDelete={() => onDeleteStatus(status.id)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  },
);

StatusGroup.displayName = "StatusGroup";
